import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Badge,
  Box,
  IconButton,
  Popover,
  Typography,
  List,
  ListItem,
  ListItemText,
  Button,
  Divider,
  Tooltip,
} from '@mui/material'
import { Notifications, NotificationsActive, EventBusy } from '@mui/icons-material'
import {
  markAsRead,
  markAllAsRead,
} from '../../redux/slices/Notifications/notificationsSlice'

const timeAgo = dateStr => {
  const diff = Date.now() - new Date(dateStr).getTime()
  const minutes = Math.floor(diff / 60000)
  const hours = Math.floor(minutes / 60)
  const days = Math.floor(hours / 24)

  const rtf = new Intl.RelativeTimeFormat('es', { numeric: 'auto' })
  if (days > 0) return rtf.format(-days, 'day')
  if (hours > 0) return rtf.format(-hours, 'hour')
  if (minutes > 0) return rtf.format(-minutes, 'minute')
  return 'hace un momento'
}

const NotificationItem = ({ notification, onMarkRead }) => {
  const isUnread = !notification.readAt

  const handleClick = () => {
    if (isUnread) onMarkRead(notification.id)
  }

  return (
    <ListItem
      alignItems="flex-start"
      onClick={handleClick}
      sx={{
        cursor: isUnread ? 'pointer' : 'default',
        backgroundColor: isUnread ? '#f0f7ff' : 'transparent',
        borderLeft: isUnread ? '3px solid #1976d2' : '3px solid transparent',
        transition: 'background-color 0.2s',
        '&:hover': isUnread ? { backgroundColor: '#e3f0ff' } : {},
        gap: 1,
        py: 1.5,
        px: 2,
      }}>
      <EventBusy fontSize="small" color="warning" sx={{ mt: 0.5, flexShrink: 0 }} />
      <ListItemText
        primary={
          <Typography
            variant="body2"
            fontWeight={isUnread ? 700 : 400}
            color="text.primary">
            {notification.title}
          </Typography>
        }
        secondary={
          <>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              sx={{ mt: 0.25 }}>
              {notification.body}
            </Typography>
            <Typography
              variant="caption"
              color="text.disabled"
              display="block"
              sx={{ mt: 0.5 }}>
              {timeAgo(notification.createdAt)}
            </Typography>
          </>
        }
        sx={{ m: 0 }}
      />
    </ListItem>
  )
}

const NotificationBell = () => {
  const dispatch = useDispatch()
  const { items, unreadCount } = useSelector(state => state.notifications)
  const [anchorEl, setAnchorEl] = useState(null)

  const open = Boolean(anchorEl)

  const handleOpen = event => setAnchorEl(event.currentTarget)
  const handleClose = () => setAnchorEl(null)

  const handleMarkRead = id => dispatch(markAsRead(id))
  const handleMarkAll = () => dispatch(markAllAsRead())

  return (
    <>
      <Tooltip title="Notificaciones">
        <IconButton onClick={handleOpen} size="small" sx={{ ml: 1 }}>
          <Badge badgeContent={unreadCount} color="error" max={9}>
            {unreadCount > 0 ? (
              <NotificationsActive fontSize="small" sx={{ color: '#fff' }} />
            ) : (
              <Notifications fontSize="small" sx={{ color: 'rgba(255,255,255,0.7)' }} />
            )}
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          elevation: 3,
          sx: {
            width: 360,
            maxHeight: 480,
            borderRadius: 2,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          },
        }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
            flexShrink: 0,
          }}>
          <Typography variant="subtitle2" fontWeight={700} color="primary.main">
            Notificaciones
          </Typography>
          {unreadCount > 0 && (
            <Button
              size="small"
              onClick={handleMarkAll}
              sx={{ fontSize: '0.75rem', py: 0 }}>
              Marcar todas como leídas
            </Button>
          )}
        </Box>

        <Box sx={{ overflowY: 'auto', flex: 1 }}>
          {items.length === 0 ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Notifications sx={{ fontSize: 36, color: 'text.disabled', mb: 1 }} />
              <Typography variant="body2" color="text.disabled">
                Sin notificaciones
              </Typography>
            </Box>
          ) : (
            <List disablePadding>
              {items.map((notification, index) => (
                <React.Fragment key={notification.id}>
                  <NotificationItem
                    notification={notification}
                    onMarkRead={handleMarkRead}
                  />
                  {index < items.length - 1 && <Divider component="li" />}
                </React.Fragment>
              ))}
            </List>
          )}
        </Box>
      </Popover>
    </>
  )
}

export default NotificationBell
